import { FieldArray, Formik, useFormikContext } from "formik";
import { Trash2 } from "lucide-react";
import * as Yup from "yup";

import { getInventory } from "src/api/inventory";
import ComboSearch from "src/components/ComboSearch";
import { Field, TextField } from "src/components/form";
import EmptyState from "src/components/common/EmptyState";
import { Button } from "src/components/ui/button";
import { Label } from "src/components/ui/label";
import type { ProductRow } from "src/types/inventory";

export interface StockItem {
	product_id: string;
	qty: number;
	product: ProductRow | null;
}

export interface StockItemsFormValues {
	items: StockItem[];
}

const pickerSchema = Yup.object().shape({
	product_id: Yup.string().required("Please select a product"),
	product: Yup.mixed().nonNullable(),
});

/** Validation for one row — exported so each form can compose it into its own schema. */
export const stockItemValidator = Yup.object().shape({
	product_id: Yup.string().required("Please select a product"),
	qty: Yup.number().moreThan(0, "Quantity must be greater than 0"),
});

interface ProductItemsFieldProps {
	/** Show the SKU alongside each product name (stock transfers do, stock updates don't). */
	showSku?: boolean;
	emptyDescription?: string;
}

/**
 * Product picker + editable quantity list, shared by the stock-update and stock-request
 * forms. Adding a product already in the list bumps its quantity instead of duplicating it.
 */
const ProductItemsField = ({ showSku, emptyDescription = "Search for a product above to add it." }: ProductItemsFieldProps) => {
	const { values } = useFormikContext<StockItemsFormValues>();

	return (
		<FieldArray
			name="items"
			render={({ push, remove, replace }) => (
				<div className="tw-flex tw-flex-1 tw-flex-col tw-gap-4">
					<ProductPicker items={values.items} push={push} replace={replace} showSku={showSku} />

					{values.items.length === 0 ? (
						<EmptyState title="No items yet" description={emptyDescription} />
					) : (
						<div className="tw-divide-y tw-divide-border tw-rounded-[10px] tw-border tw-border-border tw-px-4">
							{values.items.map((_, index) => (
								<StockItemRow key={index} index={index} showSku={showSku} remove={() => remove(index)} />
							))}
						</div>
					)}
				</div>
			)}
		/>
	);
};

interface ProductPickerProps {
	items: StockItem[];
	push: (item: StockItem) => void;
	replace: (index: number, item: StockItem) => void;
	showSku?: boolean;
}

const ProductPicker = ({ items, push, replace, showSku }: ProductPickerProps) => (
	<Formik
		initialValues={{ product_id: "", qty: 1, product: null } as StockItem}
		validationSchema={pickerSchema}
		onSubmit={(values, { resetForm }) => {
			const existingIndex = items.findIndex(({ product_id }) => product_id === values.product_id);

			if (existingIndex >= 0) {
				const existing = items[existingIndex];
				replace(existingIndex, { ...existing, qty: existing.qty + 1 });
			} else {
				push(values);
			}

			resetForm();
		}}
	>
		{({ values, setFieldValue, submitForm, errors, touched }) => (
			<div className="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-start">
				<Field
					label="Product"
					name="product_id"
					className="tw-flex-1"
					error={touched.product_id && errors.product_id}
					hint="Pick from your inventory list"
				>
					<ComboSearch
						value={values.product_id}
						onChange={(value: string, option: ProductRow) => {
							setFieldValue("product_id", value);
							setFieldValue("product", option);
						}}
						placeholder="Search product..."
						className="tw-h-11 tw-w-full tw-justify-between"
						optionRenderer={({ name, sku }: ProductRow) => <p>{showSku ? `${name} (SKU: ${sku})` : name}</p>}
						queryKey="items"
						queryFN={getInventory}
						mapFN={(option: ProductRow) => ({
							label: option.name,
							value: String(option.id),
							info: option,
						})}
					/>
				</Field>

				<div className="tw-grid tw-gap-1.5 sm:tw-w-32">
					<Label className="tw-hidden sm:tw-invisible sm:tw-block">Product</Label>
					<Button type="button" onClick={submitForm} variant="secondary" className="tw-h-11 tw-w-full">
						Add item
					</Button>
				</div>
			</div>
		)}
	</Formik>
);

const StockItemRow = ({ index, showSku, remove }: { index: number; showSku?: boolean; remove: () => void }) => {
	const { values, handleChange } = useFormikContext<StockItemsFormValues>();

	const item = values.items[index];

	return (
		<div className="tw-flex tw-items-center tw-gap-3 tw-py-3">
			<div className="tw-min-w-0 tw-flex-1">
				<p className="tw-truncate tw-text-[14px] tw-font-medium tw-text-text">{item?.product?.name}</p>
				{showSku && <p className="tw-text-[12px] tw-text-text-soft">SKU: {item?.product?.sku ?? "N/A"}</p>}
			</div>

			<TextField
				name={`items.${index}.qty`}
				value={item.qty}
				onChange={handleChange}
				type="number"
				inputMode="numeric"
				min={1}
				aria-label="Quantity"
				className="tw-h-9 tw-w-[84px] tw-text-center"
			/>

			<Button
				type="button"
				onClick={remove}
				variant="ghost"
				size="icon"
				aria-label="Remove item"
				className="tw-shrink-0 tw-text-text-soft hover:tw-text-red"
			>
				<Trash2 className="tw-size-4" />
			</Button>
		</div>
	);
};

export default ProductItemsField;
